import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { prestationService } from '../services/api';
import type { Prestation, CategoryPrestationRate } from '../types';

// State interfaces
interface PrestationState {
  prestations: Prestation[];
  rates: CategoryPrestationRate[];
  loading: boolean;
  error: string | null;
}

const initialState: PrestationState = {
  prestations: [],
  rates: [],
  loading: false,
  error: null,
};

// Async thunks for Prestations
export const fetchPrestations = createAsyncThunk(
  'prestation/fetchPrestations',
  async (params: { type?: 'OPTION' | 'ASSURANCE'; is_active?: boolean } | undefined = undefined, { rejectWithValue }) => {
    try {
      const response = await prestationService.getPrestations(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch prestations');
    }
  }
);

export const createPrestation = createAsyncThunk(
  'prestation/createPrestation',
  async (data: Partial<Prestation>, { rejectWithValue }) => {
    try {
      const response = await prestationService.createPrestation(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create prestation');
    }
  }
);

export const updatePrestation = createAsyncThunk(
  'prestation/updatePrestation',
  async ({ id, data }: { id: number; data: Partial<Prestation> }, { rejectWithValue }) => {
    try {
      const response = await prestationService.updatePrestation(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update prestation');
    }
  }
);

export const deletePrestation = createAsyncThunk(
  'prestation/deletePrestation',
  async (id: number, { rejectWithValue }) => {
    try {
      await prestationService.deletePrestation(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete prestation');
    }
  }
);

// Async thunks for Category Rates
export const fetchRates = createAsyncThunk(
  'prestation/fetchRates',
  async (params: { prestation_id?: number; category_id?: number; tier_id?: number } | undefined = undefined, { rejectWithValue }) => {
    try {
      const response = await prestationService.getCategoryRates(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rates');
    }
  }
);

export const createRate = createAsyncThunk(
  'prestation/createRate',
  async (data: Partial<CategoryPrestationRate>, { rejectWithValue }) => {
    try {
      const response = await prestationService.createCategoryRate(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create rate');
    }
  }
);

export const updateRate = createAsyncThunk(
  'prestation/updateRate',
  async ({ id, data }: { id: number; data: Partial<CategoryPrestationRate> }, { rejectWithValue }) => {
    try {
      const response = await prestationService.updateCategoryRate(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update rate');
    }
  }
);

export const deleteRate = createAsyncThunk(
  'prestation/deleteRate',
  async (id: number, { rejectWithValue }) => {
    try {
      await prestationService.deleteCategoryRate(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete rate');
    }
  }
);

// Slice
const prestationSlice = createSlice({
  name: 'prestation',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Prestations
    builder
      .addCase(fetchPrestations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrestations.fulfilled, (state, action) => {
        state.loading = false;
        state.prestations = action.payload;
      })
      .addCase(fetchPrestations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPrestation.fulfilled, (state, action) => {
        state.prestations.push(action.payload);
      })
      .addCase(updatePrestation.fulfilled, (state, action) => {
        const index = state.prestations.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.prestations[index] = action.payload;
        }
      })
      .addCase(deletePrestation.fulfilled, (state, action) => {
        state.prestations = state.prestations.filter(p => p.id !== action.payload);
      });

    // Rates
    builder
      .addCase(fetchRates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRates.fulfilled, (state, action) => {
        state.loading = false;
        state.rates = action.payload;
      })
      .addCase(fetchRates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createRate.fulfilled, (state, action) => {
        state.rates.push(action.payload);
      })
      .addCase(updateRate.fulfilled, (state, action) => {
        const index = state.rates.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.rates[index] = action.payload;
        }
      })
      .addCase(deleteRate.fulfilled, (state, action) => {
        state.rates = state.rates.filter(r => r.id !== action.payload);
      });
  },
});

export const { clearError } = prestationSlice.actions;
export default prestationSlice.reducer;

